import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// Auth contexts
import { AuthProvider, useAuth }       from './context/AuthContext';
import { GodAuthProvider, useGodAuth } from './context/GodAuthContext';

// Tenant pages
import AppShell          from './components/AppShell';
import Login             from './pages/Login';
import ResetPassword     from './pages/ResetPassword';
import SetupWizard       from './pages/SetupWizard';
import TosAcceptance     from './pages/TosAcceptance';
import { SuperAdminDashboard, AdminDashboard, EmployeeDashboard } from './pages/Dashboards';
import Notifications     from './pages/Notifications';
import Settings          from './pages/Settings';
import Departments       from './pages/Departments';
import Employees         from './pages/Employees';
import EmployeeProfile   from './pages/EmployeeProfile';
import DeletionApprovals from './pages/DeletionApprovals';
import Tasks             from './pages/Tasks';
import CreateTask        from './pages/CreateTask';
import EditTask          from './pages/EditTask';
import TaskDetail        from './pages/TaskDetail';
import Team              from './pages/Team';
import Pool              from './pages/Pool';
import DevTools          from './pages/DevTools';
import CriticalTasks     from './pages/CriticalTasks';
import DeadlineMissed    from './pages/DeadlineMissed';
import ActivityLog       from './pages/ActivityLog';
import AdminActionsLog   from './pages/AdminActionsLog';
import Reports           from './pages/Reports';
import PersonalNotepad   from './pages/PersonalNotepad';
import SecurityAuditLog  from './pages/SecurityAuditLog';
import Holidays         from './pages/Holidays';
import Announcements    from './pages/Announcements';
import Reminders        from './pages/Reminders';
import BulkTaskUpload   from './pages/BulkTaskUpload';
import MfaVerify        from './pages/MfaVerify';
import MfaSetup         from './pages/MfaSetup';
import MyAccount        from './pages/MyAccount';
import Watermark        from './components/Watermark';

// God Mode pages
import GodLogin          from './pages/god/GodLogin';
import GodShell          from './pages/god/GodShell';
import GodDashboard      from './pages/god/GodDashboard';
import GodCompanies      from './pages/god/GodCompanies';
import GodCompanyDetail  from './pages/god/GodCompanyDetail';
import GodAuditLog       from './pages/god/GodAuditLog';
import GodOffboarding    from './pages/god/GodOffboarding';

import { Spinner } from './components/ui';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

function Loader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#0f172a' }}>
      <Spinner size={36} />
    </div>
  );
}

function dashboardFor(role) {
  const map = {
    SUPER_ADMIN: '/dashboard/super-admin',
    ADMIN:       '/dashboard/admin',
    EMPLOYEE:    '/dashboard/employee',
  };
  return map[role] || '/dashboard/employee';
}

// ── Tenant route guards ────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;

  // Step 1 — must reset password
  if (user.forceReset) return <Navigate to="/reset-password" replace />;

  // Step 2 — must accept ToS (only after password is set)
  if (!user.tosAccepted) return <Navigate to="/tos" replace />;

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user && !user.forceReset && user.tosAccepted) {
    return <Navigate to={dashboardFor(user.role)} replace />;
  }
  return children;
}

// ToS route guard — must be logged in, must NOT have accepted yet
function TosRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user)             return <Navigate to="/login"              replace />;
  if (user.forceReset)   return <Navigate to="/reset-password"     replace />;
  if (user.tosAccepted)  return <Navigate to={dashboardFor(user.role)} replace />;
  return children;
}

// ── God Mode route guards ──────────────────────────────────────────────────────

function GodProtectedRoute({ children }) {
  const { godUser, loading } = useGodAuth();
  if (loading) return <Loader />;
  if (!godUser) return <Navigate to="/god/login" replace />;
  return children;
}

function GodPublicRoute({ children }) {
  const { godUser, loading } = useGodAuth();
  if (loading) return <Loader />;
  if (godUser) return <Navigate to="/god/dashboard" replace />;
  return children;
}

// ── MFA Setup page — shown when company policy forces setup before app access ──
function MfaSetupPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const handleComplete = async () => {
    await refreshUser();
    if (user?.role === 'SUPER_ADMIN') navigate('/dashboard/super-admin', { replace: true });
    else if (user?.role === 'ADMIN')  navigate('/dashboard/admin',       { replace: true });
    else                               navigate('/dashboard/employee',    { replace: true });
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '440px', background: '#fff', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#EF4444', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Required by your company</p>
        <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#111827', letterSpacing: '-0.04em', marginBottom: '8px' }}>Set up two-factor authentication</h2>
        <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '24px', lineHeight: 1.65 }}>
          Your company requires all employees to use two-factor authentication. Set it up now to continue.
        </p>
        <MfaSetup onComplete={handleComplete} onCancel={null} />
      </div>
    </div>
  );
}

// ── Route tree ─────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      {/* ── God Mode ─────────────────────────────────────────────── */}
      <Route path="/god/login" element={<GodPublicRoute><GodLogin /></GodPublicRoute>} />
      <Route path="/god" element={<GodProtectedRoute><GodShell /></GodProtectedRoute>}>
        <Route index                element={<Navigate to="/god/dashboard" replace />} />
        <Route path="dashboard"     element={<GodDashboard />} />
        <Route path="companies"     element={<GodCompanies />} />
        <Route path="companies/:id" element={<GodCompanyDetail />} />
        <Route path="offboarding"   element={<GodOffboarding />} />
        <Route path="audit-log"     element={<GodAuditLog />} />
      </Route>

      {/* ── Tenant public ────────────────────────────────────────── */}
      <Route path="/login"          element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/mfa-verify"      element={<MfaVerify />} />
      <Route path="/mfa-setup"       element={<MfaSetupPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/setup"          element={<ProtectedRoute><SetupWizard /></ProtectedRoute>} />

      {/* ── ToS acceptance — between reset-password and app ──────── */}
      <Route path="/tos" element={<TosRoute><TosAcceptance /></TosRoute>} />

      {/* ── Tenant protected app ─────────────────────────────────── */}
      <Route path="/" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard/super-admin" replace />} />

        <Route path="dashboard/super-admin" element={<SuperAdminDashboard />} />
        <Route path="dashboard/admin"       element={<AdminDashboard />} />
        <Route path="dashboard/employee"    element={<EmployeeDashboard />} />

        <Route path="settings"    element={<Settings />} />
        <Route path="departments" element={<Departments />} />

        <Route path="tasks/critical" element={<CriticalTasks />} />
        <Route path="tasks/overdue"  element={<DeadlineMissed />} />
        <Route path="tasks/create"   element={<CreateTask />} />
        <Route path="tasks/:id/edit" element={<EditTask />} />
        <Route path="tasks/:id"      element={<TaskDetail />} />
        <Route path="tasks"          element={<Tasks />} />

        <Route path="employees"          element={<Employees />} />
        <Route path="employees/:id"      element={<EmployeeProfile />} />
        <Route path="deletion-approvals" element={<DeletionApprovals />} />

        <Route path="pool"  element={<Pool />} />
        <Route path="team"  element={<Team />} />

        <Route path="logs/activity"      element={<ActivityLog />} />
        <Route path="logs/admin-actions" element={<AdminActionsLog />} />

        <Route path="reports"   element={<Reports />} />
        <Route path="dev-tools" element={<DevTools />} />
        <Route path="notepad"   element={<PersonalNotepad />} />
        <Route path="holidays"       element={<Holidays />} />
        <Route path="announcements"  element={<Announcements />} />
        <Route path="reminders"      element={<Reminders />} />
        <Route path="tasks/bulk"     element={<BulkTaskUpload />} />
        <Route path="my-account"       element={<MyAccount />} />
        <Route path="logs/security" element={<SecurityAuditLog />} />

        <Route path="notifications" element={<Notifications />} />
        <Route path="more" element={<EmployeeDashboard />} />
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <GodAuthProvider>
          <AuthProvider>
            <Watermark />
            <AppRoutes />
          </AuthProvider>
        </GodAuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
